import { css, StyleSheet } from 'aphrodite';
import { EditorContent } from '@tiptap/react';
import * as React from 'react';
import { ISupportThread } from '../../../models/Support/ISupportThread';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { useEditNoteMutation } from '../../../queries/Support/useSupportThreadMutations';
import { getApiErrorMessage } from '../../../utils/Support/supportHelpers';
import { Button } from '../../../web/components-v2/Button';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { ComposerSuggestionMenu } from './TicketComposer/ComposerSuggestionMenu';
import { composerStyles } from './TicketComposer/composerStyles';
import { ComposerEditorCallbacks, getMentionedUserIds, useComposerEditor } from './TicketComposer/useComposerEditor';
import { useComposerSuggestions } from './TicketComposer/useComposerSuggestions';
import { hydrateMentionsForEditor, type ThreadBodyMention } from './threadBodyHtml';
import './styles/composerEditor.css';

interface NoteEditFormProps {
	thread: ISupportThread;
	/** Scopes the `@` roster and `/` saved replies to the ticket's mailbox. */
	ticket: ISupportTicket;
	/** The note's current mentions with display labels, so the editor can rebuild them as mention nodes. */
	mentions: ThreadBodyMention[];
	currentUserId: string;
	currentUserName: string;
	currentAccountId?: string;
	onDone: () => void;
}

// Files are not uploaded from here: images already in the note stay, and new attachments belong in a new note.
const ignoreFiles = (): void => undefined;

export const NoteEditForm: React.FC<NoteEditFormProps> = ({
	thread,
	ticket,
	mentions,
	currentUserId,
	currentUserName,
	currentAccountId,
	onDone,
}) => {
	const editNote = useEditNoteMutation(thread.ticketId);
	const editorWrapRef = React.useRef<HTMLDivElement>(null);
	const [isEmpty, setIsEmpty] = React.useState(false);
	// Read once: re-seeding on a poll would wipe what the author is typing.
	const [initialHtml] = React.useState(() => hydrateMentionsForEditor(thread.body || '', mentions));

	const suggestions = useComposerSuggestions({
		agentName: currentUserName,
		currentAccountId,
		currentUserId,
		editorWrapRef,
		ticket,
	});

	const editorCallbacks: ComposerEditorCallbacks = {
		...suggestions.editorCallbacks,
		onUpdate: instance => setIsEmpty(instance.isEmpty),
		getPlaceholder: () => 'Internal note — team only…',
		isNoteMode: () => true,
		focusFormattingBar: () => false,
		onLinkClick: (): void => undefined,
		insertFiles: ignoreFiles,
	};
	const editor = useComposerEditor(editorCallbacks, initialHtml);

	React.useEffect(() => {
		editor?.commands.focus('end');
	}, [editor]);

	const save = () => {
		if (!editor || editor.isEmpty || editNote.isPending) return;
		editNote.mutate(
			{ threadId: thread.id, body: editor.getHTML(), mentionedUserIds: getMentionedUserIds(editor) },
			{ onSuccess: onDone }
		);
	};

	// The editor handles keys first; an open `@` menu consumes Escape and Enter to close or pick.
	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.defaultPrevented || suggestions.isOpen) return;
		if (event.key === 'Escape') {
			event.preventDefault();
			onDone();
		} else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
			event.preventDefault();
			save();
		}
	};

	return (
		<div className={css(styles.form)} onKeyDown={handleKeyDown}>
			<div ref={editorWrapRef} className={css(composerStyles.editorWrap, styles.editorWrap)}>
				<div
					className={css(styles.editorScroll)}
					onMouseDown={event => {
						if (event.target === event.currentTarget) {
							event.preventDefault();
							editor?.commands.focus('end');
						}
					}}
				>
					<EditorContent editor={editor} aria-label='Edit note' />
				</div>
				{suggestions.menu && (
					<ComposerSuggestionMenu
						ref={suggestions.menuRef}
						menu={suggestions.menu}
						items={suggestions.items}
						onPick={suggestions.pick}
					/>
				)}
			</div>
			{editNote.isError && (
				<div role='alert' className={css(styles.error)}>
					{getApiErrorMessage(editNote.error, 'Failed to save the note. Please try again.')}
				</div>
			)}
			<div className={css(styles.actions)}>
				<Button variant='ghost' size='sm' onClick={onDone} disabled={editNote.isPending}>
					Cancel
				</Button>
				<Button variant='primary' size='sm' onClick={save} isLoading={editNote.isPending} disabled={isEmpty}>
					Save
				</Button>
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
		marginTop: SPACE[8],
	},
	editorWrap: {
		border: `1px solid ${THEME.colors.border.main}`,
		borderRadius: THEME.common.borderRadius,
		background: THEME.colors.background.main,
	},
	editorScroll: {
		overflowY: 'auto',
		minHeight: 80,
		maxHeight: 300,
		cursor: 'text',
	},
	error: {
		fontSize: THEME.typography.fontSizes.xs,
		color: THEME.colors.danger.main,
	},
	actions: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: SPACE[8],
	},
});
